import { Component, computed, effect, inject, linkedSignal, resource, signal, viewChild } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MatDatepicker } from "@angular/material/datepicker";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { NgSelectComponent, NgSelectModule } from "@ng-select/ng-select";
import { ICellEditorAngularComp } from "ag-grid-angular";
import { ICellEditorParams } from "ag-grid-community";
import { firstValueFrom } from "rxjs";
import { Ticket } from "src/app/_models";
import { ProjectService, TicketService } from "src/app/_services";
import { TicketSearchItem } from "../types";

@Component({
  selector: "ticket-grid-status-editor",
  template: `
    <ng-select
      class="bm-join-item w-[300px]"
      [items]="projectStatuses.value()"
      bindLabel="name"
      bindValue="id"
      [ngModel]="statusId()"
      (ngModelChange)="statusId.set($event)"
      [loading]="projectStatuses.isLoading()"
      [required]="true"
    >
      <ng-template ng-label-tmp let-item="item">
        <div class="d-flex align-items-center gap-1">
          @if (item.closed) {
            <span class="fa fa-check-circle text-success"></span>
          }
          @if (item.canceled) {
            <span class="fa fa-ban text-error"></span>
          }
          @if (item.report_closed) {
            <span class="fa fa-hand-paper-o text-muted"></span>
          }
          <span>{{ item.name }}</span>
        </div>
      </ng-template>
    </ng-select>
    <button
      class="flex-none bm-join-item bm-btn bm-btn-primary bm-btn-square bm-tooltip"
      [attr.data-tip]="'Speichern'"
      (click)="save()"
      [disabled]="isSaving() || isLoading()"
    >
      <fa-icon [icon]="['far', 'save']" />
    </button>
    <button
      class="flex-none bm-join-item bm-btn bm-btn-square bm-tooltip"
      [attr.data-tip]="'Verwerfen'"
      (click)="cancel()"
      [disabled]="isSaving() || isLoading()"
    >
      <fa-icon [icon]="['far', 'times']" />
    </button>
  `,
  imports: [FormsModule, NgSelectModule, FontAwesomeModule],
  host: {
    class: "bm-join",
  },
})
export class TicketGridStatusEditorComponent implements ICellEditorAngularComp {
  protected projectService = inject(ProjectService);
  protected ticketService = inject(TicketService);

  protected params = signal<ICellEditorParams<TicketSearchItem>>(null);
  protected data = computed(() => this.params()?.data);
  protected projectId = computed(() => this.data()?.project_id);
  protected projectStatuses = resource({
    request: this.projectId,
    loader: async ({ request }) => {
      return firstValueFrom(this.projectService.getStatuses(request));
    },
  });
  protected isSaving = signal(false);
  protected isLoading = this.projectStatuses.isLoading;

  protected statusId = linkedSignal<number>(() => this.data()?.status_hash?.status_id);
  protected value = linkedSignal<string>(() => this.params()?.value);
  protected picker = viewChild(NgSelectComponent);

  public agInit(params: ICellEditorParams) {
    this.params.set(params);
  }

  public refresh(params: ICellEditorParams) {
    this.params.set(params);
    return true;
  }

  public getValue() {
    return this.value();
  }

  public afterGuiAttached(): void {
    this.picker().open();
  }

  public isPopup(): boolean {
    return true; // editor doesn't fit into a cell, so it should be a popup
  }

  public async save() {
    this.isSaving.set(true);
    const id = this.data().id;
    const status = this.projectStatuses.value().find((it) => it.id === this.statusId());
    const params: Pick<Ticket, "status_id"> = {
      status_id: this.statusId(),
    };
    firstValueFrom(this.ticketService.updateTicket(String(id), params))
      .then(() => {
        this.value.set(status.name);
        this.params().stopEditing();
      })
      .catch(() => {
        // Handle error, e.g., show a notification
        console.error("Failed to save ticket status");
      })
      .finally(() => {
        this.isSaving.set(false);
      });
  }

  public cancel() {
    this.params().stopEditing();
  }

  // getPopupPosition?(): "over" | "under" | undefined {
  //   throw new Error("Method not implemented.");
  // }
  // isCancelBeforeStart?(): boolean {
  //   throw new Error("Method not implemented.");
  // }
  // isCancelAfterEnd?(): boolean {
  //   throw new Error("Method not implemented.");
  // }
  // focusIn?(): void {
  //   throw new Error("Method not implemented.");
  // }
  // focusOut?(): void {
  //   throw new Error("Method not implemented.");
  // }
}
